type Ctx = CanvasRenderingContext2D

interface VirtualCanvas {
    width: number
    height: number
    draw(ctx: Ctx, x: number, y: number): void
}

var orphan_canvas = document.createElement('canvas')
var orphan_ctx = orphan_canvas.getContext('2d')!
if (!orphan_ctx) throw new Error('could not init orphan_ctx')

export type Font = {
    size: number
    name: string
    bold?: true
}

function font({size, name, bold}: Font): string {
    return `${bold ? 'bold ' : ''}${size}px ${name}`
}

export type Padding = {
    top: number
    left: number
    right: number
    bottom: number
}

type VirtualLabelProps = {
    text: string
    font: Font
    color: string
    max_width?: number
    pad?: Partial<Padding>
    dir?: 'ltr' | 'rtl'
    added_line_height?: number
}
/** Virtual Context */
export class VirtualLabel implements VirtualCanvas {
    #w: number
    #h: number
    // #y: number
    #font: Font
    #color: string
    #lines: string[]
    #lh: number
    #pad: Padding
    #dir: 'ltr' | 'rtl'

    constructor(P: VirtualLabelProps) {
        this.#font = P.font
        this.#color = P.color
        this.#dir = P.dir || 'ltr'
        this.#pad = {
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            ...(P.pad || {}),
        }

        orphan_ctx.direction = this.#dir
        orphan_ctx.font = font(this.#font)
        orphan_ctx.textBaseline = 'middle'
        // let m = orphan_ctx.measureText(P.text)

        // this.#lh = m.actualBoundingBoxAscent + m.actualBoundingBoxDescent
        this.#lh = this.#font.size + (P.added_line_height || 0)
        // this.#lh += this.#pad.top + this.#pad.bottom
        // this.#lh += P.font[0]

        let lp = this.#pad.right + this.#pad.left
        let bp = this.#pad.top + this.#pad.bottom

        this.#lines = P.text.split('\n')

        if (P.max_width) {
            let mx = P.max_width - lp

            for (let [ldx, l] of this.#lines.entries()) {
                let lines: string[] = []
                let words = l.split(' ')
                let curr_line = words[0]!
                for (let w of words.slice(1)) {
                    let tm = orphan_ctx.measureText(curr_line + ' ' + w)
                    if (tm.width < mx) {
                        curr_line += ' ' + w
                    } else {
                        lines.push(curr_line)
                        curr_line = w
                    }
                }
                lines.push(curr_line)
                this.#lines.splice(ldx, 1, ...lines)
            }
        }

        let mw = 0
        for (let l of this.#lines) {
            let tm = orphan_ctx.measureText(l)
            if (tm.width > mw) mw = tm.width
        }
        this.#w = mw + lp

        this.#h = this.#lines.length * this.#lh + bp

        // this.#y = m.actualBoundingBoxAscent
    }

    draw(ctx: Ctx, x: number, y: number) {
        let og_dir = ctx.direction
        let og_font = ctx.font
        let og_tbl = ctx.textBaseline
        let og_fill_style = ctx.fillStyle

        ctx.direction = this.#dir
        ctx.font = font(this.#font)
        ctx.textBaseline = 'middle'
        ctx.fillStyle = this.#color

        let dx = x + this.#pad.left
        if (this.#dir == 'rtl') {
            dx = x - this.#pad.right
        }

        for (let [i, l] of this.#lines.entries()) {
            ctx.fillText(l, dx, y + this.#pad.top + this.#lh / 2 + i * this.#lh)
        }

        ctx.direction = og_dir
        ctx.font = og_font
        ctx.textBaseline = og_tbl
        ctx.fillStyle = og_fill_style
    }

    get width() {
        return this.#w
    }

    get height() {
        return this.#h
    }
}

export type VirtualTableProps = {
    header: string
    rows: [string, string][]
    width: number
    color: string

    vfont: Font
    kfont: Font
    hfont: Font
    hpad?: Partial<Padding>
    vpad?: Partial<Padding>
    kpad?: Partial<Padding>
    hbg?: string
    kbg?: string
    vbg?: string
    line_width: number
    center_value?: boolean
}
export class VirtualTable implements VirtualCanvas {
    #w: number
    #h: number
    #header: VirtualLabel
    #rows: [VirtualLabel, VirtualLabel, number][]
    #valw: number
    #keyw: number
    #color: string
    #hbg?: string
    #kbg?: string
    #vbg?: string
    #line_width: number
    #center_value: boolean

    constructor(P: VirtualTableProps) {
        this.#color = P.color
        this.#center_value = !!P.center_value
        this.#line_width = P.line_width
        this.#header = new VirtualLabel({
            dir: 'rtl',
            max_width: P.width,
            color: P.color,
            font: P.hfont,
            text: P.header,
            pad: P.hpad,
        })
        this.#rows = []
        this.#w = P.width

        this.#hbg = P.hbg
        this.#kbg = P.kbg
        this.#vbg = P.vbg

        this.#valw = this.#w * 0.55
        this.#keyw = this.#w - this.#valw
        this.#h = this.#header.height

        for (let [k, v] of P.rows) {
            let vv = new VirtualLabel({
                max_width: this.#valw,
                text: v,
                font: P.vfont,
                color: P.color,
                pad: P.vpad,
                dir: 'rtl',
            })

            let kv = new VirtualLabel({
                max_width: this.#keyw,
                text: k,
                font: P.kfont,
                color: P.color,
                pad: P.kpad,
                dir: 'rtl',
            })

            let rh = Math.max(vv.height, kv.height)
            this.#h += rh
            this.#rows.push([kv, vv, rh])
        }
    }

    draw(ctx: Ctx, x: number, y: number) {
        ctx.lineWidth = this.#line_width

        if (this.#hbg) {
            ctx.fillStyle = this.#hbg
            ctx.fillRect(x, y, this.#w, this.#header.height)
        }

        if (this.#vbg) {
            ctx.fillStyle = this.#vbg
            ctx.fillRect(
                x,
                y + this.#header.height,
                this.#valw,
                this.#h - this.#header.height
            )
        }

        if (this.#kbg) {
            ctx.fillStyle = this.#kbg
            ctx.fillRect(
                x + this.#valw,
                y + this.#header.height,
                this.#keyw,
                this.#h - this.#header.height
            )
        }

        this.#header.draw(ctx, x + (this.#w + this.#header.width) / 2, y)

        ctx.beginPath()

        ctx.moveTo(x, y + this.#header.height)
        ctx.lineTo(x + this.#w, y + this.#header.height)

        ctx.moveTo(x + this.#valw, y + this.#header.height)
        ctx.lineTo(x + this.#valw, y + this.#h)

        let ry = y + this.#header.height
        for (let [k, v, rh] of this.#rows) {
            k.draw(ctx, x + this.#w, ry + (rh - k.height) / 2)
            let vx = x + this.#valw
            if (this.#center_value) {
                vx = x + (this.#valw + v.width) / 2
            }
            v.draw(ctx, vx, ry + (rh - v.height) / 2)
            ctx.moveTo(x, ry)
            ctx.lineTo(x + this.#w, ry)
            ry += rh
        }

        ctx.closePath()
        ctx.strokeStyle = this.#color

        ctx.stroke()

        ctx.strokeRect(x, y, this.#w, this.#h)

    }

    get width() {
        return this.#w
    }

    get height() {
        return this.#h
    }
}

type VirtualHeadBoxProps = {
    header: string
    width: number
    text: string
    color: string

    vfont: Font
    hfont: Font
    hpad?: Partial<Padding>
    vpad?: Partial<Padding>
    hbg?: string
    vbg?: string
    line_width: number
    added_line_height?: number
}
export class VirtualHeadBox implements VirtualCanvas {
    #w: number
    #h: number
    #header: VirtualLabel
    #body: VirtualLabel
    #color: string
    #hbg?: string
    #vbg?: string
    #line_width: number

    constructor(P: VirtualHeadBoxProps) {
        this.#color = P.color
        this.#line_width = P.line_width
        this.#header = new VirtualLabel({
            dir: 'rtl',
            max_width: P.width,
            color: P.color,
            font: P.hfont,
            text: P.header,
            pad: P.hpad,
        })
        this.#body = new VirtualLabel({
            dir: 'rtl',
            max_width: P.width,
            color: P.color,
            font: P.vfont,
            text: P.text,
            pad: P.vpad,
            added_line_height: P.added_line_height
        })
        this.#w = P.width

        this.#hbg = P.hbg
        this.#vbg = P.vbg

        this.#h = this.#header.height + this.#body.height
    }

    draw(ctx: Ctx, x: number, y: number) {
        ctx.lineWidth = this.#line_width

        if (this.#hbg) {
            ctx.fillStyle = this.#hbg
            ctx.fillRect(x, y, this.#w, this.#header.height)
        }

        if (this.#vbg) {
            ctx.fillStyle = this.#vbg
            ctx.fillRect(x, y + this.#header.height, this.#w, this.#body.height)
        }

        ctx.beginPath()

        ctx.moveTo(x, y + this.#header.height)
        ctx.lineTo(x + this.#w, y + this.#header.height)

        ctx.closePath()
        ctx.strokeStyle = this.#color
        ctx.stroke()

        this.#header.draw(ctx, x + (this.#w + this.#header.width) / 2, y)

        this.#body.draw(ctx, x + this.#w, y + this.#header.height)

        ctx.strokeStyle = this.#color
        ctx.strokeRect(x, y, this.#w, this.#h)
    }

    get header_height() {
        return this.#header.height
    }

    get width() {
        return this.#w
    }

    get height() {
        return this.#h
    }
}
